import React, { useState } from "react";
import styled from "styled-components";

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;

  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const Tooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid rgba(0, 0, 0, 0.8);
  }
`;

const defaultFormatter = (v) => Math.round(v).toLocaleString();

const SearchVolumeChart = ({ data, loading, valueFormatter = defaultFormatter }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  if (loading) {
    return <LoadingMessage>Loading chart...</LoadingMessage>;
  }

  if (!data || data.length === 0) {
    return <LoadingMessage>No search volume data available.</LoadingMessage>;
  }

  // Simple SVG line chart
  const width = 800;
  const height = 300;
  const padding = 40;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const minDate = new Date(Math.min(...data.map((d) => new Date(d.date))));
  const maxDate = new Date(Math.max(...data.map((d) => new Date(d.date))));
  const dateRange = maxDate - minDate || 1;

  const getX = (date) => {
    const dateObj = new Date(date);
    return padding + ((dateObj - minDate) / dateRange) * chartWidth;
  };

  const getY = (count) => {
    return height - padding - (count / maxCount) * chartHeight;
  };

  const points = data
    .map((d) => `${getX(d.date)},${getY(d.count)}`)
    .join(" ");

  const handleMouseEnter = (d, event) => {
    const svg = event.currentTarget.ownerSVGElement;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const svgPoint = point.matrixTransform(svg.getScreenCTM().inverse());
    
    setHoveredPoint(d);
    // Position tooltip above the point, slightly offset
    setTooltipPosition({
      x: getX(d.date),
      y: getY(d.count) - 20,
    });
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  return (
    <ChartContainer>
      <ChartSvg viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
          <g key={ratio}>
            <line
              x1={padding}
              y1={padding + ratio * chartHeight}
              x2={width - padding}
              y2={padding + ratio * chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
            <text
              x={padding - 10}
              y={padding + ratio * chartHeight + 5}
              fontSize="12"
              fill="#666"
              textAnchor="end"
            >
              {valueFormatter(maxCount * (1 - ratio))}
            </text>
          </g>
        ))}

        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="#4caf50"
          strokeWidth="2"
        />

        {/* Points */}
        {data.map((d, i) => (
          <circle
            key={i}
            cx={getX(d.date)}
            cy={getY(d.count)}
            r="4"
            fill="#4caf50"
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => handleMouseEnter(d, e)}
            onMouseLeave={handleMouseLeave}
          />
        ))}

        {/* Tooltip - render after other elements so it appears on top */}
        {hoveredPoint && (
          <g style={{ pointerEvents: "none" }}>
            {/* Highlight circle */}
            <circle
              cx={getX(hoveredPoint.date)}
              cy={getY(hoveredPoint.count)}
              r="6"
              fill="#4caf50"
              opacity="0.5"
            />
            {/* Background rectangle */}
            <rect
              x={getX(hoveredPoint.date) - 25}
              y={getY(hoveredPoint.count) - 35}
              width="50"
              height="20"
              fill="rgba(0, 0, 0, 0.85)"
              rx="4"
              stroke="#4caf50"
              strokeWidth="1"
            />
            {/* Tooltip text */}
            <text
              x={getX(hoveredPoint.date)}
              y={getY(hoveredPoint.count) - 20}
              fontSize="12"
              fill="white"
              textAnchor="middle"
              fontWeight="bold"
            >
              {valueFormatter(hoveredPoint.count)}
            </text>
          </g>
        )}

        {/* Axes */}
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="#333"
          strokeWidth="2"
        />
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          stroke="#333"
          strokeWidth="2"
        />

        {/* X-axis date labels */}
        {data.length > 0 && (() => {
          // Show labels for a reasonable number of points (max 10)
          const labelInterval = Math.max(1, Math.floor(data.length / 10));
          return data
            .filter((_, i) => i % labelInterval === 0 || i === data.length - 1)
            .map((d, i) => {
              const date = new Date(d.date);
              const dateStr = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
              const x = getX(d.date);
              return (
                <g key={i}>
                  <line
                    x1={x}
                    y1={height - padding}
                    x2={x}
                    y2={height - padding + 5}
                    stroke="#333"
                    strokeWidth="1"
                  />
                  <text
                    x={x}
                    y={height - padding + 20}
                    fontSize="11"
                    fill="#666"
                    textAnchor="middle"
                  >
                    {dateStr}
                  </text>
                </g>
              );
            });
        })()}
      </ChartSvg>
    </ChartContainer>
  );
};

export default SearchVolumeChart;

